import React, { useState } from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { object, string } from 'yup'
import { AxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { Dialog, InfoBox, useSnackbarContext } from '../../components'
import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  RadioGroupControl,
  SubmitButton,
  TextFieldControl,
} from '../../components/form'
import { ApiErrorResponse, CreateOperationRequestParams } from '../../types.ts'
import { useMutateCreateOperationRequest } from '../../services'

import { OfdFields } from './ofd-fields.tsx'
import { useUser } from '@/stores/slices/user-slice.ts'

const OTP_FORM = 'oneTimePageForm'
const otpFormValidationSchema = object<FormSchema<CreateOperationRequestParams>>({
  clientName: string().required(FormSchemaMessages.required),
  clientEmail: string().required(FormSchemaMessages.required).test(FormSchemaTests.isEmail),
  orderSum: string().required(FormSchemaMessages.required).test(FormSchemaTests.isNotZero),
  ofdPaymentMethodType: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
  ofdPaymentSubjectType: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
  ofdName: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
})

export const NewOperationRequestDialog = (): React.ReactElement => {
  const { addSnack } = useSnackbarContext()
  const qc = useQueryClient()
  const user = useUser()

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const handleTogglePopup = (visible: boolean) => () => setShowCreateDialog(visible)
  const { mutateAsync, reset } = useMutateCreateOperationRequest()

  const handleOTPFormSubmit = async ({
    ofdPaymentMethodType,
    ofdPaymentSubjectType,
    ofdName,
    ofdNomenclatureCode,
    ...rest
  }: CreateOperationRequestParams) => {
    try {
      await mutateAsync({
        ...rest,
        ...(rest.ofdBillMode === 'AUTO'
          ? { ofdNomenclatureCode, ofdPaymentSubjectType, ofdPaymentMethodType, ofdName }
          : {}),
      })

      addSnack({
        severity: 'success',
        message: 'Страница успешно создана',
        timeout: 6000,
      })
      qc.invalidateQueries({ queryKey: ['operation_requests'] })
      setShowCreateDialog(false)
      reset()
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorMessage) {
        errorMessage = error.response.data.errorMessage
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
      console.log(axiosError)
    }
  }
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Button onClick={handleTogglePopup(true)}>Создать страницу оплаты</Button>
      {showCreateDialog && (
        <Form<CreateOperationRequestParams>
          name={OTP_FORM}
          onSubmit={handleOTPFormSubmit}
          validationSchema={otpFormValidationSchema}
          initialValues={{
            ofdBillMode: 'MANUAL',
            ofdPaymentMethodType: 'FULL_PREPAYMENT',
            ofdPaymentSubjectType: 'GOOD',
          }}
        >
          <Dialog
            open
            maxWidth="md"
            title="Создать страницу оплаты"
            onClose={handleTogglePopup(false)}
            content={
              <>
                <InfoBox severity="error">
                  <Typography>Срок жизни страницы оплаты 48 часов.</Typography>
                </InfoBox>
                <Grid container flexDirection="column" spacing={2} mt={2}>
                  <Grid item xs={12}>
                    <TextFieldControl<CreateOperationRequestParams> name="clientName" fullWidth label="ФИО клиента" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldControl<CreateOperationRequestParams>
                      name="clientEmail"
                      fullWidth
                      label="Email клиента"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldControl<CreateOperationRequestParams>
                      name="orderNumber"
                      fullWidth
                      label="Номер заказа магазина"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldControl<CreateOperationRequestParams>
                      name="orderSum"
                      fullWidth
                      label="Сумма"
                      normalize={InputNormalizers.positiveNumbersOnly()}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldControl<CreateOperationRequestParams> name="productCode" fullWidth label="Продукт" />
                  </Grid>
                  {user?.permissions?.ofd && (
                    <>
                      <Grid item xs={12}>
                        <RadioGroupControl<CreateOperationRequestParams>
                          name="ofdBillMode"
                          revalidateFields={[
                            'ofdPaymentMethodType',
                            'ofdNomenclatureCode',
                            'ofdPaymentSubjectType',
                            'ofdName',
                          ]}
                          row
                          label="Режим формирования чека"
                          items={[
                            { title: 'Использовать указанные ниже', value: 'AUTO' },
                            { title: 'Не используется', value: 'MANUAL' },
                          ]}
                        />
                      </Grid>
                      <OfdFields />
                    </>
                  )}
                </Grid>
              </>
            }
            actions={<SubmitButton form={OTP_FORM}>Создать</SubmitButton>}
          />
        </Form>
      )}
    </Box>
  )
}
